import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Divider } from '@mui/material';
import { LoadingTypography } from 'lib/LoadingTypography';
import { isDefined, isNumber } from 'lib/typeInference';
import { useCurrencyString } from 'lib/useCurrencyString';
import clsx from 'clsx';

const PREFIX = 'PayItemsHeader';

const classes = {
    title: `${PREFIX}title`,
    subtitle: `${PREFIX}subtitle`,
    small: `${PREFIX}small`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.title}`]: {
        fontSize: '24px',
        paddingBottom: theme.spacing(1),
        fontWeight: 500,
        color: theme.palette.text.primary
    },

    [`& .${classes.small}`]: {
        fontSize: '1.2rem'
    },

    [`& .${classes.subtitle}`]: {
        fontSize: '18px',
        paddingBottom: theme.spacing(0.5),
        fontWeight: 500,
        color: theme.palette.text.primary,
        opacity: 0.5
    }
}));

interface PayItemsHeaderProps {
    variant?: 'default' | 'small';
    loading?: boolean;
    total?: number;
    paid?: boolean;
    splitTotal?: number;
    customSplit?: number;
}

export const PayItemsHeader: React.FC<React.PropsWithChildren<PayItemsHeaderProps>> = ({
    loading,
    total,
    paid,
    variant,
    splitTotal,
    customSplit
}) => {
    const { t } = useTranslation();
    const getCurrencyString = useCurrencyString();
    const isSplitting = isDefined(splitTotal);
    const label = React.useMemo(() => {
        if (paid) {
            return t('PAT_QUICKPAY_YOU_PAID');
        }
        if (isSplitting) {
            return t('PAT_QUICKPAY_PAY_YOUR_BILL_SHARE');
        }
        return t('PAT_QUICKPAY_PAY_YOUR_BILL');
    }, [isSplitting, paid, t]);
    const content = React.useMemo(() => {
        if (isNumber(customSplit)) {
            return getCurrencyString(customSplit);
        }
        if (isSplitting && isDefined(splitTotal)) {
            return getCurrencyString(splitTotal);
        }
        if (isDefined(total)) {
            return getCurrencyString(total);
        }
        return '';
    }, [customSplit, getCurrencyString, isSplitting, splitTotal, total]);
    return (
        <StyledBox>
            {(isSplitting || isNumber(customSplit)) && (
                <React.Fragment>
                    <Box display="flex" justifyContent="space-between" pt={0.5}>
                        <LoadingTypography className={classes.subtitle} loading={loading} width={130}>
                            {t('BILL_CHECKOUT_PAYMENT_INFO_TOTAL')}
                        </LoadingTypography>
                        <LoadingTypography className={classes.subtitle} loading={loading} width={80}>
                            {isDefined(total) && getCurrencyString(total)}
                        </LoadingTypography>
                    </Box>
                    <Box>
                        <Divider />
                    </Box>
                </React.Fragment>
            )}
            <Box display="flex" justifyContent="space-between" pt={1}>
                <LoadingTypography
                    className={clsx(classes.title, { [classes.small]: variant === 'small' })}
                    loading={loading}
                    width={130}
                >
                    {label}
                </LoadingTypography>
                <LoadingTypography
                    className={clsx(classes.title, { [classes.small]: variant === 'small' })}
                    loading={loading}
                    width={80}
                >
                    {content}
                </LoadingTypography>
            </Box>
            <Divider />
        </StyledBox>
    );
};
